import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useRequest from './useRequest';
import { normalizeMetalObjectPurity } from '../domain/contracts/metalObjectPurity';
import {
    getKaratOptionsForMetal,
    getMetalTypeOptions,
    normalizeMetalKaratageOptions,
    normalizeMetalPriceOptions,
    type MetalKaratageOption,
    type MetalPriceOption,
    type RawMetalKaratageOption,
    type RawMetalPriceOption,
} from '../domain/contracts/metalPriceOptions';
import {
    ELECTRONICS_TYPE,
    appendCustomMetalRows,
    applyCustomMetalSelection,
    applyElectronicObjectChange,
    applyElectronicsAnnex,
    applyLocalMetalCalculations,
    applySingleObjectPrice,
    createDefaultElectronicObjects,
    createDefaultMetalObjects,
    isEligibleForPriceCalc,
    selectDeskPresets,
    type DeskElectronicObject,
    type DeskMetalObject,
    type SingleObjectPrice,
} from '../domain/contracts/pawnObjectDesk';

const QUANTITY_PRICE_DEBOUNCE_MS = 500;

interface UsePawnObjectDeskOptions {
    /** The workstation's cash point; the desk stays empty until it is known. */
    cashPointId: number | null;
    exchangeRate: number;
    extendedDuration: boolean;
    /**
     * Rebuilds the per-counter `current_obj<cashPointId>` scratch table before loading the
     * presets, as contract creation does. Verification only needs the presets.
     */
    prepareScratchTable: boolean;
    /** Called when the price service cannot be reached, so the page can say so. */
    onPriceCalculationOffline?: () => void;
}

export interface PawnObjectDesk {
    metalObjects: DeskMetalObject[];
    electronicObjects: DeskElectronicObject[];
    isLoaded: boolean;
    metalTypeOptions: string[];
    getCustomKaratOptions: (metalType: string) => number[];
    handleMetalObjectChange: (index: number, field: keyof DeskMetalObject, value: unknown) => void;
    handleCustomMetalSelection: (index: number, metalType: string, karat: number) => void;
    handleElectronicObjectChange: (
        index: number,
        field: keyof DeskElectronicObject,
        value: unknown,
    ) => void;
    electronicsModalOpen: boolean;
    openElectronicsModal: (index: number) => void;
    closeElectronicsModal: () => void;
    saveElectronicsModal: (objectName: string, pawnPrice: string) => void;
    /** Clears every row back to an untouched desk (Delphi `Prepare_Desk`). */
    reset: () => void;
}

const updateAt = <T>(items: T[], index: number, update: (item: T) => T): T[] =>
    items.map((item, itemIndex) => (itemIndex === index ? update(item) : item));

/**
 * State and behaviour of the pawning object desk, shared by contract creation and by
 * previous-session verification so both assess an object with the same rules.
 */
export default function usePawnObjectDesk({
    cashPointId,
    exchangeRate,
    extendedDuration,
    prepareScratchTable,
    onPriceCalculationOffline,
}: UsePawnObjectDeskOptions): PawnObjectDesk {
    const request = useRequest();
    const [metalObjects, setMetalObjects] = useState<DeskMetalObject[]>([]);
    const [electronicObjects, setElectronicObjects] = useState<DeskElectronicObject[]>(
        createDefaultElectronicObjects,
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [metalPriceOptions, setMetalPriceOptions] = useState<MetalPriceOption[]>([]);
    const [metalKaratageOptions, setMetalKaratageOptions] = useState<MetalKaratageOption[]>([]);
    // Objects with a server price calculation in flight, so local edits do not race it
    const [calculatingObjects, setCalculatingObjects] = useState<Set<number>>(new Set());
    const [activeElectronicsRowIndex, setActiveElectronicsRowIndex] = useState<number | null>(null);

    const presetMetalObjectsRef = useRef<DeskMetalObject[]>([]);
    const metalObjectsRef = useRef(metalObjects);
    const exchangeRateRef = useRef(exchangeRate);
    const priceCalculationTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
    const offlineCallbackRef = useRef(onPriceCalculationOffline);

    // Debounced and asynchronous calculations must see the latest rows and rate, not the
    // values captured when they were scheduled.
    useEffect(() => {
        metalObjectsRef.current = metalObjects;
        exchangeRateRef.current = exchangeRate;
        offlineCallbackRef.current = onPriceCalculationOffline;
    }, [metalObjects, exchangeRate, onPriceCalculationOffline]);

    useEffect(() => {
        const loadMetalPriceOptions = async () => {
            try {
                const response = await request<{ data?: { prices?: RawMetalPriceOption[] } }>({
                    path: '/contract-objects/metal-prices',
                    method: 'GET',
                });
                setMetalPriceOptions(normalizeMetalPriceOptions(response?.data?.prices ?? []));
            } catch (error) {
                console.error('Load metal price options error:', error);
                setMetalPriceOptions([]);
            }
        };
        const loadMetalKaratageOptions = async () => {
            try {
                const response = await request<{ data?: RawMetalKaratageOption[] }>({
                    path: '/metal-price-config/purity-levels',
                    method: 'GET',
                });
                setMetalKaratageOptions(normalizeMetalKaratageOptions(response?.data ?? []));
            } catch (error) {
                console.error('Load metal karatage options error:', error);
                setMetalKaratageOptions([]);
            }
        };

        void loadMetalPriceOptions();
        void loadMetalKaratageOptions();
    }, [request]);

    useEffect(() => {
        // `current_obj<cashPointId>` is a per-counter scratch table; guessing a cash point
        // would let two counters overwrite each other, so loading waits until it is known.
        if (isLoaded || cashPointId === null) {
            return;
        }

        const loadConfiguredObjects = async () => {
            let presets = createDefaultMetalObjects();
            try {
                if (prepareScratchTable) {
                    await request({
                        path: `/contract-objects/prepare-table/${cashPointId}`,
                        method: 'POST',
                    });
                }
                const configResponse = await request<{ data: DeskMetalObject[] }>({
                    path: '/contract-objects/configured-objects',
                    method: 'GET',
                });
                const configured = selectDeskPresets(configResponse?.data ?? []);
                if (configured.length > 0) {
                    presets = configured.map(normalizeMetalObjectPurity);
                }
            } catch (error) {
                console.error('Error loading configured objects:', error);
            }
            presetMetalObjectsRef.current = presets;
            setMetalObjects(appendCustomMetalRows(presets));
            setIsLoaded(true);
        };

        void loadConfiguredObjects();
    }, [cashPointId, isLoaded, prepareScratchTable, request]);

    const clearPriceTimers = useCallback(() => {
        Object.values(priceCalculationTimersRef.current).forEach(clearTimeout);
        priceCalculationTimersRef.current = {};
    }, []);

    useEffect(() => clearPriceTimers, [clearPriceTimers]);

    const markCalculating = (index: number, calculating: boolean) => {
        setCalculatingObjects(previous => {
            const next = new Set(previous);
            if (calculating) next.add(index);
            else next.delete(index);
            return next;
        });
    };

    const calculateSingleObjectPrice = async (index: number, objData: DeskMetalObject) => {
        markCalculating(index, true);
        try {
            const priceResponse = await request<{ data: SingleObjectPrice }>({
                path: '/contract-objects/calculate-single-price',
                method: 'POST',
                params: {
                    metalType: objData.tipMetal,
                    karat: objData.karat,
                    quantity: objData.cantitate,
                    stonePercent: objData.piatraPercent,
                    extendedDuration,
                },
            });

            if (priceResponse?.data) {
                setMetalObjects(previous =>
                    updateAt(previous, index, obj =>
                        applySingleObjectPrice(obj, priceResponse.data, exchangeRateRef.current),
                    ),
                );
            }
        } catch (error) {
            console.error('Error calculating single object price:', error);
            // Prices can still be entered by hand; only a network failure is worth a message
            if (error instanceof Error && error.message.includes('fetch')) {
                offlineCallbackRef.current?.();
            }
        } finally {
            markCalculating(index, false);
        }
    };

    const scheduleDebouncedQuantityCalc = (index: number, quantity: number) => {
        clearTimeout(priceCalculationTimersRef.current[index]);
        priceCalculationTimersRef.current[index] = setTimeout(() => {
            const updatedObj = { ...metalObjectsRef.current[index], cantitate: quantity };
            if (isEligibleForPriceCalc(updatedObj)) {
                void calculateSingleObjectPrice(index, updatedObj);
            }
        }, QUANTITY_PRICE_DEBOUNCE_MS);
    };

    const handleMetalObjectChange = (index: number, field: keyof DeskMetalObject, value: unknown) => {
        const apiCalculating = calculatingObjects.has(index);
        setMetalObjects(previous =>
            updateAt(previous, index, obj =>
                applyLocalMetalCalculations(obj, field, value, exchangeRate, apiCalculating),
            ),
        );

        if (field === 'cantitate') {
            scheduleDebouncedQuantityCalc(index, Number(value));
            return;
        }

        if (field === 'tipMetal' || field === 'karat') {
            const updatedObj: DeskMetalObject = { ...metalObjects[index], [field]: value };
            if (isEligibleForPriceCalc(updatedObj)) {
                void calculateSingleObjectPrice(index, updatedObj);
            }
        }
    };

    const handleCustomMetalSelection = (index: number, metalType: string, karat: number) => {
        const pricing = {
            priceOptions: metalPriceOptions,
            karatageOptions: metalKaratageOptions,
            extendedDuration,
            exchangeRate,
            apiCalculating: calculatingObjects.has(index),
        };
        setMetalObjects(previous =>
            updateAt(previous, index, obj => applyCustomMetalSelection(obj, metalType, karat, pricing)),
        );
    };

    const handleElectronicObjectChange = (
        index: number,
        field: keyof DeskElectronicObject,
        value: unknown,
    ) => {
        setElectronicObjects(previous =>
            updateAt(previous, index, obj =>
                applyElectronicObjectChange(obj, field, value, exchangeRate),
            ),
        );
    };

    // Opens the electronics annex, as Delphi's LineEnabler does for a ticked electronics row
    const openElectronicsModal = (index: number) => {
        const electronicObject = electronicObjects[index];
        if (electronicObject?.enabled && electronicObject.tip === ELECTRONICS_TYPE) {
            setActiveElectronicsRowIndex(index);
        }
    };

    const closeElectronicsModal = () => setActiveElectronicsRowIndex(null);

    const saveElectronicsModal = (objectName: string, pawnPrice: string) => {
        if (activeElectronicsRowIndex === null) {
            return;
        }
        const rowIndex = activeElectronicsRowIndex;
        setElectronicObjects(previous =>
            updateAt(previous, rowIndex, obj =>
                applyElectronicsAnnex(obj, objectName, pawnPrice, exchangeRate),
            ),
        );
        setActiveElectronicsRowIndex(null);
    };

    const reset = useCallback(() => {
        clearPriceTimers();
        setCalculatingObjects(new Set());
        setActiveElectronicsRowIndex(null);
        setMetalObjects(appendCustomMetalRows(presetMetalObjectsRef.current));
        setElectronicObjects(createDefaultElectronicObjects());
    }, [clearPriceTimers]);

    const metalTypeOptions = useMemo(() => getMetalTypeOptions(metalPriceOptions), [metalPriceOptions]);

    const getCustomKaratOptions = useCallback(
        (metalType: string) => getKaratOptionsForMetal(metalKaratageOptions, metalType),
        [metalKaratageOptions],
    );

    return {
        metalObjects,
        electronicObjects,
        isLoaded,
        metalTypeOptions,
        getCustomKaratOptions,
        handleMetalObjectChange,
        handleCustomMetalSelection,
        handleElectronicObjectChange,
        electronicsModalOpen: activeElectronicsRowIndex !== null,
        openElectronicsModal,
        closeElectronicsModal,
        saveElectronicsModal,
        reset,
    };
}
